import { BusinessObjectBase, Command, CommandHandler, ConsoleLogger, ModelBase, RepositoryBase, RepositoryResponseCode, RepositorySaveResponse } from '@skylab-shared/core';

import { CommandHandlerFor } from '@skylab-angular-shared/core';

export interface FormSaveCommandParams<TRepo extends RepositoryBase<TBO, TModel>, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase> {
    repo: TRepo;
    bo: TBO;
    canSaveAsync?: () => Promise<boolean>;
}

export interface FormSaveCommandResult {
    state: RepositoryResponseCode;
    data: { version: string };
}

export class FormSaveCommand<
    TRepo extends RepositoryBase<TBO, TModel>,
    TBO extends BusinessObjectBase<TModel>,
    TModel extends ModelBase>
    extends Command<FormSaveCommandParams<TRepo, TBO, TModel>, RepositorySaveResponse> { }

@CommandHandlerFor(FormSaveCommand)
export class FormSaveCommandHandler<
    TRepo extends RepositoryBase<TBO, TModel>,
    TBO extends BusinessObjectBase<TModel>,
    TModel extends ModelBase>
    implements CommandHandler<FormSaveCommand<TRepo, TBO, TModel>, FormSaveCommandParams<TRepo, TBO, TModel>, RepositorySaveResponse> {

    private logger: ConsoleLogger;

    public constructor() {
        this.logger = new ConsoleLogger(this.constructor.name);
    }

    public async handleAsync(command: FormSaveCommand<TRepo, TBO, TModel>): Promise<RepositorySaveResponse> {
        const params = command.params;
        if (!params) {
            throw new Error('Params are required.');
        }
        let response: Readonly<RepositorySaveResponse>;
        try {
            const canSave = params.canSaveAsync && await params.canSaveAsync();
            if (!canSave && params.canSaveAsync) throw new Error(`Can't execute save.`);

            await params.bo.waitForProcessingCompletionAsync();
            if (!params.bo.isValid) throw new Error(`Can't execute save.`);

            response = await params.repo.saveAsync(params.bo);
        } catch (err: any) {
            this.logger.error(err);
            response = {
                code: err?.code ?? null
            };
        }

        return response;
    }

}
